import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "../components/PageShell";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Loader2, Building2, MapPin, Phone, Mail, User, Inbox as InboxIcon, Target, Users, Bell } from "lucide-react";

const initials = (n) => (n || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

const AdminInbox = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [notifCount, setNotifCount] = useState(0);
  const notifRef = useRef(null);

  useEffect(() => {
    if (!authLoading && (!user || user.role !== "admin")) navigate(user ? "/" : "/login");
  }, [authLoading, user, navigate]);

  const loadInbox = useCallback(() => {
    api.get("/admin/inbox").then((r) => setGroups(r.data)).finally(() => setLoading(false));
  }, []);

  const loadCount = useCallback(() => {
    api.get("/notifications/unread-count").then((r) => setNotifCount(r.data.count || 0)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.role === "admin") {
      loadInbox();
      loadCount();
      const t = setInterval(loadCount, 30000);
      return () => clearInterval(t);
    }
  }, [user, loadInbox, loadCount]);

  useEffect(() => {
    const onClick = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggleNotif = async () => {
    const next = !notifOpen;
    setNotifOpen(next);
    if (next) {
      const r = await api.get("/notifications").catch(() => ({ data: [] }));
      setNotifs(r.data || []);
      if (notifCount > 0) {
        await api.post("/notifications/read-all").catch(() => {});
        setNotifCount(0);
      }
      loadInbox();
    }
  };

  if (authLoading || !user || user.role !== "admin")
    return <PageShell><div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#1E4FA3]" /></div></PageShell>;

  return (
    <PageShell>
      <div className="mx-auto max-w-[1000px] px-6 py-10 lg:px-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <InboxIcon className="h-6 w-6 text-[#1E4FA3]" />
              <h1 className="font-heading text-[32px] font-extrabold text-[#0B2545] sm:text-[38px]">Community Inbox</h1>
            </div>
            <p className="mt-1 text-[16px] text-slate-600">Showing only openings with interested professionals — plus the private contacts to connect them.</p>
          </div>

          <div className="relative shrink-0" ref={notifRef}>
            <button
              onClick={toggleNotif}
              data-testid="admin-notification-button"
              aria-label="Notifications"
              className="relative flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-50"
            >
              <Bell className="h-5 w-5" />
              {notifCount > 0 && (
                <span data-testid="admin-notification-badge" className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#16A34A] px-1 text-[11px] font-bold text-white">
                  {notifCount > 9 ? "9+" : notifCount}
                </span>
              )}
            </button>
            {notifOpen && (
              <div data-testid="admin-notification-panel" className="absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-xl">
                <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
                  <Bell className="h-4 w-4 text-[#16A34A]" />
                  <span className="font-heading text-sm font-bold text-[#0B2545]">New interests</span>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifs.length === 0 ? (
                    <div className="px-4 py-10 text-center text-sm text-slate-500">No interests yet. You'll be alerted here when a professional shows interest.</div>
                  ) : (
                    notifs.map((n) => (
                      <div key={n.id} data-testid="admin-notification-item" className={`border-b border-slate-50 px-4 py-3 ${n.read ? "" : "bg-[#16A34A]/5"}`}>
                        <p className="text-sm font-semibold text-[#0B2545]">{n.title}</p>
                        <p className="mt-0.5 text-xs text-slate-600">{n.body}</p>
                        <p className="mt-1 text-[11px] text-slate-400">{new Date(n.created_at).toLocaleString()}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#1E4FA3]" /></div>
        ) : groups.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center">
            <p className="text-slate-500">No interested professionals yet. Openings appear here once someone expresses interest.</p>
            <Button onClick={() => navigate("/admin")} className="mt-4 h-11 rounded-xl bg-[#1E4FA3] px-6 font-semibold text-white hover:bg-[#193f83]">Post Community Openings</Button>
          </div>
        ) : (
          <div className="mt-6 space-y-6">
            {groups.map((g) => {
              const c = g.institution_contact || {};
              return (
                <div key={g.group_id} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6">
                  <div className="flex flex-col gap-3 border-b border-slate-100 pb-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex items-start gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#1E4FA3]/10"><Building2 className="h-5 w-5 text-[#1E4FA3]" /></div>
                      <div>
                        <h2 className="font-heading text-lg font-bold text-[#0B2545]">{g.institution_name}</h2>
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                          {c.name && <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" />{c.name}</span>}
                          {c.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{c.phone}</span>}
                          {c.email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{c.email}</span>}
                        </div>
                        <p className="mt-1 text-[11px] font-medium uppercase tracking-wide text-amber-600">Private — admin only</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 space-y-4">
                    {g.openings.map((o) => (
                      <div key={o.id} className="rounded-xl border border-slate-100 p-4">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <h3 className="font-heading text-[16px] font-bold text-[#0B2545]">{o.role_title}</h3>
                            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-sm text-slate-500">
                              <span className="rounded-full bg-[#1E4FA3]/10 px-2.5 py-0.5 text-xs font-semibold text-[#1E4FA3]">{o.qualification}</span>
                              {o.location && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{o.location}</span>}
                            </div>
                          </div>
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#16A34A]/10 px-3 py-1 text-xs font-semibold text-[#16A34A]"><Users className="h-3.5 w-3.5" />{o.interested_count} interested</span>
                        </div>

                        {o.interested.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {o.interested.map((it) => {
                              const p = it.professional || {};
                              const prefs = p.preferences || {};
                              return (
                                <div key={it.id} className="flex flex-col gap-2 rounded-lg bg-slate-50 p-3 sm:flex-row sm:items-start sm:justify-between">
                                  <div className="flex items-start gap-3">
                                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#16A34A]/10 font-heading text-xs font-bold text-[#16A34A]">{initials(it.professional_name)}</div>
                                    <div>
                                      <p className="font-semibold text-[#0B2545]">{it.professional_name}{p.title ? ` · ${p.title}` : ""}</p>
                                      <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500">
                                        {it.professional_email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{it.professional_email}</span>}
                                        {p.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{p.location}</span>}
                                      </div>
                                      {prefs.desired_role && <p className="mt-1 flex items-center gap-1 text-xs text-slate-600"><Target className="h-3.5 w-3.5 text-[#16A34A]" />Seeking: {prefs.desired_role}</p>}
                                    </div>
                                  </div>
                                  <span className="shrink-0 text-[11px] text-slate-400">{new Date(it.created_at).toLocaleDateString()}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default AdminInbox;
